(() => {
  const optOutKey = 'lussovi_shipping_protection_opt_out';
  const timerKey = 'lussovi_cart_timer_end';
  const drawerSelector = 'cart-drawer';
  let syncing = false;
  let timerInterval;

  const getDrawer = () => document.querySelector(drawerSelector);

  const endpoint = (value) => {
    if (!value) return '';
    return value.endsWith('.js') ? value : `${value}.js`;
  };

  const getConfig = () => {
    const drawer = getDrawer();
    if (!drawer) return null;
    return {
      drawer,
      cartUrl: endpoint(drawer.dataset.cartUrl),
      addUrl: endpoint(drawer.dataset.cartAddUrl),
      changeUrl: endpoint(drawer.dataset.cartChangeUrl),
      protectionId: Number(drawer.dataset.protectionVariantId || 0),
      socksId: Number(drawer.dataset.socksVariantId || 0),
      currency: drawer.dataset.currency || window.Shopify?.currency?.active || 'USD',
    };
  };

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch (error) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  };

  const startTimer = (hasMerchandise) => {
    if (!hasMerchandise) {
      sessionStorage.removeItem(timerKey);
      return;
    }
    if (!sessionStorage.getItem(timerKey)) {
      sessionStorage.setItem(timerKey, String(Date.now() + 10 * 60 * 1000));
    }
  };

  const updateCountdown = () => {
    const output = getDrawer()?.querySelector('[data-lussovi-countdown]');
    if (!output) return;
    const end = Number(sessionStorage.getItem(timerKey) || Date.now());
    const remaining = Math.max(0, end - Date.now());
    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    output.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const updateProgress = (cart, config) => {
    const message = document.querySelector('[data-lussovi-shipping-message]');
    const fill = document.querySelector('[data-lussovi-shipping-fill]');
    if (!message || !fill) return;
    const qualifyingTotal = Number(cart.total_price || 0);
    const rate = Number(window.Shopify?.currency?.rate || 1);
    const threshold = Math.max(1, Math.round(10000 * rate));
    const remaining = Math.max(0, threshold - qualifyingTotal);
    const progress = Math.min(100, (qualifyingTotal / threshold) * 100);
    const remainingTemplate = message.dataset.remainingTemplate || '';
    const unlockedTemplate = message.dataset.unlockedTemplate || '';
    message.innerHTML = remaining > 0
      ? remainingTemplate.replace('[amount]', formatMoney(remaining, config.currency))
      : unlockedTemplate;
    fill.style.width = `${progress}%`;
  };

  const updateToggle = (cart, config) => {
    const toggle = document.querySelector('[data-lussovi-protection-toggle]');
    if (!toggle) return;
    const protection = cart.items.find((item) => item.variant_id === config.protectionId);
    toggle.setAttribute('aria-checked', protection ? 'true' : 'false');
    toggle.dataset.lineKey = protection?.key || '';
  };

  const fetchCart = async (config) => {
    const response = await fetch(config.cartUrl, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart request failed');
    return response.json();
  };

  const renderState = (state, config) => {
    if (state.sections && config.drawer.renderContents) {
      config.drawer.renderContents(state);
    }
  };

  const mutateCart = async (url, payload, config) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...payload,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname,
      }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    const state = await response.json();
    renderState(state, config);
    return state;
  };

  const addVariant = (id, config, properties = {}) => mutateCart(config.addUrl, {
    items: [{ id, quantity: 1, properties }],
  }, config);

  const changeLine = (id, quantity, config) => mutateCart(config.changeUrl, { id, quantity }, config);

  const refreshUi = async (config) => {
    const cart = await fetchCart(config);
    const hasMerchandise = cart.items.some((item) => item.variant_id !== config.protectionId);
    startTimer(hasMerchandise);
    updateCountdown();
    updateProgress(cart, config);
    updateToggle(cart, config);
    return cart;
  };

  const ensureProtection = async () => {
    const config = getConfig();
    if (!config || !config.protectionId || syncing) return;
    syncing = true;
    try {
      const cart = await refreshUi(config);
      const merchandise = cart.items.filter((item) => item.variant_id !== config.protectionId);
      const protection = cart.items.find((item) => item.variant_id === config.protectionId);
      if (merchandise.length === 0) {
        sessionStorage.removeItem(optOutKey);
        if (protection) await changeLine(protection.key, 0, config);
      } else if (!protection && sessionStorage.getItem(optOutKey) !== 'true') {
        await addVariant(config.protectionId, config, { _lussovi_shipping_protection: 'true' });
      } else if (protection && protection.quantity !== 1) {
        await changeLine(protection.key, 1, config);
      }
    } catch (error) {
      document.querySelector('[data-lussovi-cart-error]')?.removeAttribute('hidden');
    } finally {
      syncing = false;
      try {
        await refreshUi(config);
      } catch (error) {
        return;
      }
    }
  };

  const setProtection = async (enabled, button) => {
    const config = getConfig();
    if (!config || syncing) return;
    const previousChecked = button.getAttribute('aria-checked') === 'true';
    const previousOptOut = sessionStorage.getItem(optOutKey);
    syncing = true;
    button.setAttribute('aria-checked', enabled ? 'true' : 'false');
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    try {
      const cart = await fetchCart(config);
      const protection = cart.items.find((item) => item.variant_id === config.protectionId);
      if (enabled) {
        sessionStorage.removeItem(optOutKey);
        if (!protection) await addVariant(config.protectionId, config, { _lussovi_shipping_protection: 'true' });
      } else {
        sessionStorage.setItem(optOutKey, 'true');
        if (protection) await changeLine(protection.key, 0, config);
      }
    } catch (error) {
      button.setAttribute('aria-checked', previousChecked ? 'true' : 'false');
      if (previousOptOut === null) {
        sessionStorage.removeItem(optOutKey);
      } else {
        sessionStorage.setItem(optOutKey, previousOptOut);
      }
      document.querySelector('[data-lussovi-cart-error]')?.removeAttribute('hidden');
    } finally {
      syncing = false;
      try {
        await refreshUi(config);
      } catch (error) {
        button.setAttribute('aria-checked', previousChecked ? 'true' : 'false');
      }
      const currentButton = document.querySelector('[data-lussovi-protection-toggle]');
      if (currentButton) {
        currentButton.removeAttribute('aria-busy');
        currentButton.disabled = false;
      }
    }
  };

  document.addEventListener('click', async (event) => {
    const upsell = event.target.closest('[data-lussovi-add-upsell]');
    if (upsell) {
      const config = getConfig();
      if (!config || !config.socksId || syncing) return;
      syncing = true;
      upsell.disabled = true;
      try {
        await addVariant(config.socksId, config);
      } catch (error) {
        document.querySelector('[data-lussovi-cart-error]')?.removeAttribute('hidden');
      } finally {
        syncing = false;
        await ensureProtection();
      }
      return;
    }

    const toggle = event.target.closest('[data-lussovi-protection-toggle]');
    if (toggle) {
      await setProtection(toggle.getAttribute('aria-checked') !== 'true', toggle);
    }
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'cart-items') ensureProtection();
    });
  }

  customElements.whenDefined('cart-drawer').then(() => {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.lussoviEnhanced === 'true') return;
    drawer.dataset.lussoviEnhanced = 'true';
    const originalOpen = drawer.open.bind(drawer);
    drawer.open = (...args) => {
      originalOpen(...args);
      ensureProtection();
    };
  });

  timerInterval = window.setInterval(updateCountdown, 1000);
  window.addEventListener('pagehide', () => window.clearInterval(timerInterval), { once: true });
})();
